import { Show, createEffect, createMemo, createSignal, onCleanup, onMount } from 'solid-js';
import maplibregl, {
  type ExpressionSpecification,
  type GeoJSONSource,
  type Map as MLMap,
  type MapGeoJSONFeature,
  type MapMouseEvent,
} from 'maplibre-gl';
import { Protocol } from 'pmtiles';
import { Badge } from '~/components/primitives/badge';
import { Btn } from '~/components/primitives/btn';
import { GeolocationPrePrompt } from '~/components/geolocation-pre-prompt';
import { Glyph } from '~/components/glyph';
import {
  checkGeolocationPermission,
  geolocation,
  type GeolocationPermissionState,
  type TrackingState,
} from '~/lib/geolocation';
import { circleRing, formatDistance } from '~/lib/geo';
import { type HeatLabel, heat } from '~/lib/heat';
import { type GameNode, nodeStore, nodeToTarget } from '~/lib/nodes';
import { ENGAGEMENT_RANGE_M, nodeView } from '~/lib/node-view';
import { wakeLock } from '~/lib/wake-lock';
import { cyberpunkStyleMinimal, isPlaceholderTilesUrl, placeholderStyle } from '~/map/style';
import type { Ice, NodeRole, Target, Tone } from '~/types/game';
import 'maplibre-gl/dist/maplibre-gl.css';

/** Module-level signal so the user's pre-prompt response survives MAP
 *  tab remounts within a single page load. Reset on full reload (matches
 *  the "they get the pre-prompt again next session" rule). Consumers
 *  read it from JSX inside MapView, which provides its own reactive
 *  owner — no createRoot wrapper is required at module scope. */
const [prePromptDeclined, setPrePromptDeclined] = createSignal(false);
/** Same lifetime as `prePromptDeclined`: persists the user's "X this
 *  banner away" choice across tab-driven MAP remounts within a single
 *  page load. A full page reload (the RELOAD button's recovery path)
 *  naturally resets it back to false. */
const [bannerDismissed, setBannerDismissed] = createSignal(false);
/** How long the SIGNAL LIVE badge stays visible after the tracker first
 *  settles. The transition runs in CSS via opacity; this is the timer
 *  that triggers the fade. */
const SIGNAL_LIVE_VISIBLE_MS = 4000;

// Local GeoJSON structural types. We deliberately don't depend on
// `@types/geojson` to keep apps/web's dependency surface minimal — the
// MapLibre API only needs the shape, not the canonical types.
interface PointGeometry {
  type: 'Point';
  coordinates: [number, number];
}
interface GeoFeature<P> {
  type: 'Feature';
  geometry: PointGeometry;
  properties: P;
}
interface FeatureCollection<P> {
  type: 'FeatureCollection';
  features: GeoFeature<P>[];
}
interface PolygonFeatureCollection {
  type: 'FeatureCollection';
  features: Array<{
    type: 'Feature';
    geometry: { type: 'Polygon'; coordinates: number[][][] };
    properties: Record<string, never>;
  }>;
}

/** Properties carried on each rendered POI feature. Kept lean: styling keys
 *  the layer paints on, not the full node (the click handler looks the full
 *  node up by id from the store). */
interface PoiFeatureProps {
  id: string;
  role: NodeRole;
  inRange: boolean;
}

/** PMTiles must register exactly once per page load. Module-level flag
 *  guards against re-registration on tab switches. */
let pmtilesProtocolRegistered = false;
function ensurePMTilesProtocol(): void {
  if (pmtilesProtocolRegistered) return;
  const protocol = new Protocol();
  maplibregl.addProtocol('pmtiles', protocol.tile);
  pmtilesProtocolRegistered = true;
}

/** Fallback camera before the first GPS fix: Trinidad downtown. Zoom 16
 *  fits a single neighborhood comfortably on a phone in portrait. Once the
 *  tracker settles, the map eases to the player (see centering effect). */
const TRINIDAD_FALLBACK: [number, number] = [-104.518509, 37.171685];
const INITIAL_ZOOM = 16;

/** Source / layer IDs for runtime layers. Exported so future styling
 *  sessions and click handlers don't have to grep for strings. */
export const PLAYER_SOURCE_ID = 'player';
export const POI_SOURCE_ID = 'pois';
export const RANGE_SOURCE_ID = 'range';
export const RANGE_FILL_LAYER_ID = 'range-fill';
export const RANGE_LINE_LAYER_ID = 'range-line';
export const PLAYER_PULSE_LAYER_ID = 'player-pulse';
export const PLAYER_DOT_LAYER_ID = 'player-dot';
export const PLAYER_HEADING_LAYER_ID = 'player-heading';
export const POI_FILL_LAYER_ID = 'poi-fill';
export const POI_RING_LAYER_ID = 'poi-ring';
export const HEADING_ICON_NAME = 'player-heading-arrow';

/** Role-distinct node colours, all existing design tokens (no new colours).
 *  target = hostile magenta (hackable/danger), shop = data cyan (commerce),
 *  service = drone purple (reputation/intel). `dual` reserved (warn amber),
 *  unused this step. Shape/icon differentiation is a later visual refinement;
 *  colour + the in-range target highlight is the MVP-legible treatment. */
const ROLE_COLOR: Record<NodeRole, string> = {
  target: '#e0489c', // var(--hostile)
  shop: '#5fd0d3', // var(--data)
  service: '#9b7ee0', // var(--drone)
  dual: '#e5a13b', // var(--warn) — reserved, not emitted yet
};

/** Title-card chrome per role: the badge tone, the badge label for non-target
 *  roles, and the stub line shown in place of hack intel. */
const ROLE_BADGE_TONE: Record<NodeRole, Tone> = {
  target: 'hostile',
  shop: 'data',
  service: 'drone',
  dual: 'warn',
};
const ROLE_LABEL: Record<NodeRole, string> = {
  target: 'TARGET',
  shop: 'MARKET',
  service: 'SERVICE',
  dual: 'DUAL',
};
/** Short vendor hint shown on the card for non-target roles. */
const VENDOR_HINT: Record<NodeRole, string> = {
  target: '',
  shop: 'MARKET · tap TRADE to buy tools',
  service: 'SERVICE · tap TRADE to reduce heat',
  dual: 'DUAL · hack it or trade with it',
};

const PLAYER_DOT_COLOR = '#7fc46c'; // var(--action)
const RANGE_COLOR = '#7fc46c'; // var(--action)
const PLAYER_DOT_RADIUS = 7;
const PLAYER_PULSE_MIN_R = 8;
const PLAYER_PULSE_MAX_R = 22;
const PULSE_PERIOD_MS = 1400;

/** Data-driven colour expression keyed on the `role` property. */
const roleColorExpr: ExpressionSpecification = [
  'match',
  ['get', 'role'],
  'target',
  ROLE_COLOR.target,
  'shop',
  ROLE_COLOR.shop,
  'service',
  ROLE_COLOR.service,
  'dual',
  ROLE_COLOR.dual,
  ROLE_COLOR.target,
];

/** Hackable roles (target + dual) get the engageable map treatment; pure
 *  shops/services do not. */
const hackableExpr: ExpressionSpecification = ['in', ['get', 'role'], ['literal', ['target', 'dual']]];

/** True only for an in-range hackable node — the engageable-highlight predicate. */
const hackableInRangeExpr: ExpressionSpecification = ['all', hackableExpr, ['get', 'inRange']];

function buildPoiCollection(
  nodes: GameNode[],
  distById: Map<string, number> | null,
): FeatureCollection<PoiFeatureProps> {
  return {
    type: 'FeatureCollection',
    features: nodes.map((n) => {
      const d = distById?.get(n.id);
      return {
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [n.lng, n.lat] },
        properties: {
          id: n.id,
          role: n.role,
          inRange: d != null && d <= ENGAGEMENT_RANGE_M,
        },
      };
    }),
  };
}

function buildPlayerCollection(
  fix: { lat: number; lng: number; heading: number | null; speed: number | null } | null,
): FeatureCollection<{ heading: number; speed: number }> {
  if (!fix) return { type: 'FeatureCollection', features: [] };
  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [fix.lng, fix.lat] },
        properties: {
          heading: fix.heading ?? 0,
          speed: fix.speed ?? 0,
        },
      },
    ],
  };
}

function buildRangeCollection(fix: { lat: number; lng: number } | null): PolygonFeatureCollection {
  if (!fix) return { type: 'FeatureCollection', features: [] };
  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        geometry: { type: 'Polygon', coordinates: [circleRing(fix.lat, fix.lng, ENGAGEMENT_RANGE_M)] },
        properties: {},
      },
    ],
  };
}

/** Bake a small triangle icon for the heading arrow. Anchored at center;
 *  rotated by the feature's `heading` property at draw time. */
function makeHeadingIcon(): ImageData {
  const size = 32;
  const c = document.createElement('canvas');
  c.width = size;
  c.height = size;
  const ctx = c.getContext('2d');
  if (!ctx) throw new Error('2d canvas unavailable');
  ctx.clearRect(0, 0, size, size);
  ctx.fillStyle = PLAYER_DOT_COLOR;
  // Tip at top-center, base flanking the vertical axis. Center of the
  // image lands on the player position; tip sits about 12px above (north
  // before rotation).
  ctx.beginPath();
  ctx.moveTo(16, 2);
  ctx.lineTo(21, 13);
  ctx.lineTo(11, 13);
  ctx.closePath();
  ctx.fill();
  return ctx.getImageData(0, 0, size, size);
}

interface MapViewProps {
  /** Open the hack modal for a hackable node (target / dual). */
  onTargetSelect?: (target: Target) => void;
  /** Open the vendor (shop / service / dual) transaction panel. */
  onVendorOpen?: (node: GameNode) => void;
}

function signalBadgeFor(
  state: TrackingState,
): { label: string; tone: 'data' | 'action' | 'warn' | 'hostile'; pulse: boolean } | null {
  switch (state) {
    case 'idle':
      return { label: 'SIGNAL OFFLINE', tone: 'warn', pulse: false };
    case 'acquiring':
      return { label: 'ACQUIRING SIGNAL...', tone: 'data', pulse: true };
    case 'tracking':
      return { label: 'SIGNAL LIVE', tone: 'action', pulse: false };
    case 'error':
      return { label: 'SIGNAL ERROR', tone: 'hostile', pulse: false };
  }
}

const ICE_TONE: Record<Ice, 'data' | 'warn' | 'hostile'> = {
  LOW: 'data',
  MED: 'warn',
  HI: 'hostile',
};

const HEAT_TONE: Record<HeatLabel, 'data' | 'warn' | 'hostile'> = {
  LOW: 'data',
  ELEVATED: 'warn',
  HIGH: 'hostile',
  CRITICAL: 'hostile',
};

/** MapLibre + PMTiles map screen. Mounts a single MapLibre instance,
 *  renders the cyberpunk basemap, plots the procgen nodes fetched from the
 *  static GeoJSON, and tracks the operator's live position via the shared
 *  geolocation singleton. */
export function MapView(props: MapViewProps) {
  let containerRef: HTMLDivElement | undefined;
  const [map, setMap] = createSignal<MLMap | null>(null);
  const [mapReady, setMapReady] = createSignal(false);

  // ── Geolocation permission UX state ──────────────────────────
  const [permState, setPermState] = createSignal<GeolocationPermissionState>('prompt');
  const [showPrePrompt, setShowPrePrompt] = createSignal(false);
  const [signalLiveVisible, setSignalLiveVisible] = createSignal(true);

  const tilesUrl: string =
    (import.meta.env.VITE_TILES_URL as string | undefined) ??
    'https://tiles.example.com/colorado.pmtiles';
  const usingPlaceholder = isPlaceholderTilesUrl(tilesUrl);

  // Distance / nearest / selection state is shared with JOBS via `nodeView`
  // so the title card, range gate, ring, in-range highlight, and JOBS sort
  // all agree. The card shows the featured node (explicit selection, else
  // nearest); its distance and engageability come from the same source.
  const featured = nodeView.featured;
  const featuredDist = createMemo(() => {
    const n = featured();
    return n ? nodeView.distanceOf(n.id) : null;
  });

  // ── Permission probe + auto-start on mount ───────────────────
  // The mount-time probe drives the initial pre-prompt-vs-auto-start
  // decision only. Once the user interacts, the banner and badges are
  // driven by the live tracker state (see denied-banner gating below),
  // so a stale probe value can never sustain a wrong banner.
  const probeAndDecideMount = async () => {
    const perm = await checkGeolocationPermission();
    setPermState(perm);
    const trackerState = geolocation.state();
    if (trackerState === 'acquiring' || trackerState === 'tracking') return;
    if (perm === 'granted') {
      await wakeLock.acquire('map');
      await geolocation.start('map');
      return;
    }
    if (perm === 'denied') return;
    // 'prompt' or 'unsupported' — show the explanatory modal unless the
    // user already declined it this session.
    if (!prePromptDeclined()) setShowPrePrompt(true);
  };

  onMount(() => {
    // Fetch node data once when MAP first mounts; idempotent across remounts
    // because the store keeps the parsed set after the first success.
    if (nodeStore.status() === 'idle') void nodeStore.load();
    void probeAndDecideMount();
  });

  // No wake-lock release on MAP unmount: the singleton ref-counts owners
  // so MAP→DIAG→MAP tab switching does not churn the OS sentinel. The
  // 'map' ownership is relinquished when the tracker actually stops or
  // errors (see effect below).

  // Drop the 'map' ownership automatically when the tracker leaves an
  // active state. Tied to tracker lifecycle rather than screen lifecycle
  // so the wake lock outlives transient MAP unmounts.
  createEffect(() => {
    const s = geolocation.state();
    if (s === 'error' || s === 'idle') void wakeLock.release('map');
  });

  // When the tracker re-enters an error state (e.g. RETRY → still
  // denied), undo any prior banner dismissal so the user sees the
  // banner again rather than wondering why nothing happened.
  createEffect(() => {
    if (geolocation.state() === 'error') setBannerDismissed(false);
  });

  // ── Pre-prompt response handlers ─────────────────────────────
  const onPrePromptContinue = async () => {
    setShowPrePrompt(false);
    await wakeLock.acquire('map');
    // start() registers watchPosition which triggers the OS prompt.
    // If the user denies, the singleton transitions to 'error' with
    // lastErrorCode === 1; if they grant, to 'acquiring' → 'tracking'.
    // We don't re-probe here: the tracker is now the source of truth
    // for banner / badge visibility, and Permissions API reads racing
    // with the OS prompt are exactly what created the previous bug.
    await geolocation.start('map');
  };

  const onPrePromptDecline = () => {
    setPrePromptDeclined(true);
    setShowPrePrompt(false);
  };

  const onAcquireSignalClick = () => {
    setShowPrePrompt(true);
  };

  const [reloading, setReloading] = createSignal(false);
  const onReloadClick = () => {
    // iOS Safari caches the `denied` result of navigator.permissions.query
    // for the lifetime of the page, AND won't re-fire the geolocation
    // prompt after a deny within the same page load. A full navigation
    // is the only honest recovery — the user changes iOS Settings, taps
    // RELOAD, and the mount-time probe re-evaluates from scratch. We
    // apply the same behavior on every platform so there's only one
    // codepath to reason about.
    setReloading(true);
    window.location.reload();
  };

  // Whether the denied banner should be visible right now. Driven by
  // live tracker state plus the mount-time probe (for the case where
  // the user has previously denied and the singleton has never been
  // started, so its state is still 'idle' but we know permission is
  // 'denied' from the Permissions API).
  const showDeniedBanner = createMemo(() => {
    if (bannerDismissed()) return false;
    const trackerErrored = geolocation.state() === 'error' && geolocation.lastErrorCode() === 1;
    const probedDeniedWhileIdle = permState() === 'denied' && geolocation.state() === 'idle';
    return trackerErrored || probedDeniedWhileIdle;
  });

  // Node-data error: surfaced only when the location banner isn't already
  // claiming the top of the screen (location is the more fundamental
  // failure; a RELOAD there re-attempts the node fetch anyway).
  const showNodeError = createMemo(() => nodeStore.status() === 'error' && !showDeniedBanner());

  // ── SIGNAL LIVE auto-fade ────────────────────────────────────
  createEffect(() => {
    if (geolocation.state() === 'tracking') {
      setSignalLiveVisible(true);
      const id = setTimeout(() => setSignalLiveVisible(false), SIGNAL_LIVE_VISIBLE_MS);
      onCleanup(() => clearTimeout(id));
    } else {
      // Re-appear immediately when we drop back into acquiring / error.
      setSignalLiveVisible(true);
    }
  });

  // ── Mount / teardown ─────────────────────────────────────────
  createEffect(() => {
    if (!containerRef) return;
    ensurePMTilesProtocol();

    const style = usingPlaceholder ? placeholderStyle() : cyberpunkStyleMinimal(tilesUrl);
    const ml = new maplibregl.Map({
      container: containerRef,
      style,
      center: TRINIDAD_FALLBACK,
      zoom: INITIAL_ZOOM,
      attributionControl: false,
      // Defaults are fine for the rest; pitch/bearing locked flat per the
      // brief (no 3D, no compass). trackResize defaults to true.
    });

    // MapLibre's `load` callback is a platform event handler. The signal
    // writes inside it (setMapReady) are intentional one-shot transitions,
    // and the click handlers read stores at click time, not reactively — so
    // no solid/reactivity suppression is needed here.
    ml.on('load', () => {
      ml.addImage(HEADING_ICON_NAME, makeHeadingIcon());

      // Engagement-range ring around the player (added first so it sits
      // beneath the nodes and the player dot). Empty until the first fix.
      ml.addSource(RANGE_SOURCE_ID, { type: 'geojson', data: buildRangeCollection(null) });
      ml.addLayer({
        id: RANGE_FILL_LAYER_ID,
        type: 'fill',
        source: RANGE_SOURCE_ID,
        paint: { 'fill-color': RANGE_COLOR, 'fill-opacity': 0.06 },
      });
      ml.addLayer({
        id: RANGE_LINE_LAYER_ID,
        type: 'line',
        source: RANGE_SOURCE_ID,
        paint: {
          'line-color': RANGE_COLOR,
          'line-width': 1.2,
          'line-opacity': 0.5,
          'line-dasharray': [2, 2],
        },
      });

      // Nodes. Empty until the store resolves; the effect below fills it.
      ml.addSource(POI_SOURCE_ID, {
        type: 'geojson',
        data: buildPoiCollection([], null),
        promoteId: 'id',
      });
      ml.addLayer({
        id: POI_RING_LAYER_ID,
        type: 'circle',
        source: POI_SOURCE_ID,
        paint: {
          // Hackable nodes (target + dual) pop when in range and dim when out;
          // pure shops/services render at a steady mid treatment (always tap-able).
          'circle-radius': ['case', hackableInRangeExpr, 13, 11],
          'circle-color': 'rgba(0,0,0,0)',
          'circle-stroke-width': ['case', hackableInRangeExpr, 2, 1.4],
          'circle-stroke-color': roleColorExpr,
          'circle-stroke-opacity': [
            'case',
            hackableExpr,
            ['case', ['get', 'inRange'], 1, 0.4],
            0.7,
          ],
        },
      });
      ml.addLayer({
        id: POI_FILL_LAYER_ID,
        type: 'circle',
        source: POI_SOURCE_ID,
        paint: {
          'circle-radius': ['case', hackableInRangeExpr, 5.5, 4],
          'circle-color': roleColorExpr,
          'circle-opacity': [
            'case',
            hackableExpr,
            ['case', ['get', 'inRange'], 1, 0.5],
            0.85,
          ],
        },
      });

      ml.addSource(PLAYER_SOURCE_ID, {
        type: 'geojson',
        data: buildPlayerCollection(null),
      });
      ml.addLayer({
        id: PLAYER_PULSE_LAYER_ID,
        type: 'circle',
        source: PLAYER_SOURCE_ID,
        paint: {
          'circle-radius': PLAYER_PULSE_MIN_R,
          'circle-color': PLAYER_DOT_COLOR,
          'circle-opacity': 0.0,
          'circle-radius-transition': { duration: PULSE_PERIOD_MS / 2 },
          'circle-opacity-transition': { duration: PULSE_PERIOD_MS / 2 },
        },
      });
      ml.addLayer({
        id: PLAYER_DOT_LAYER_ID,
        type: 'circle',
        source: PLAYER_SOURCE_ID,
        paint: {
          'circle-radius': PLAYER_DOT_RADIUS,
          'circle-color': PLAYER_DOT_COLOR,
          'circle-stroke-color': '#06070a',
          'circle-stroke-width': 1.2,
          'circle-opacity': 0.4,
        },
      });
      ml.addLayer({
        id: PLAYER_HEADING_LAYER_ID,
        type: 'symbol',
        source: PLAYER_SOURCE_ID,
        filter: ['>', ['get', 'speed'], 0.5],
        layout: {
          'icon-image': HEADING_ICON_NAME,
          'icon-rotate': ['get', 'heading'],
          'icon-rotation-alignment': 'map',
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-size': 1,
        },
      });

      // Tapping any node previews it in the title card (selection persists
      // until another node is tapped or empty map is tapped). ENGAGE — not
      // the tap — opens the HackModal, subject to the range gate.
      const onPoiClick = (e: MapMouseEvent & { features?: MapGeoJSONFeature[] }) => {
        const id = e.features?.[0]?.properties?.id as string | undefined;
        if (id) nodeView.select(id);
      };
      ml.on('click', POI_FILL_LAYER_ID, onPoiClick);
      ml.on('click', POI_RING_LAYER_ID, onPoiClick);

      // Tapping empty map space clears the selection, reverting the card to
      // the nearest-node default. (The layer handlers above also fire for a
      // POI hit; this only clears when the click hit no POI.)
      ml.on('click', (e: MapMouseEvent) => {
        const hits = ml.queryRenderedFeatures(e.point, {
          layers: [POI_FILL_LAYER_ID, POI_RING_LAYER_ID],
        });
        if (hits.length === 0) nodeView.clearSelection();
      });

      // Cursor affordance on hostile POIs.
      const setCursor = (cursor: string) => {
        ml.getCanvas().style.cursor = cursor;
      };
      ml.on('mouseenter', POI_FILL_LAYER_ID, () => setCursor('pointer'));
      ml.on('mouseleave', POI_FILL_LAYER_ID, () => setCursor(''));

      setMapReady(true);
    });

    setMap(ml);

    onCleanup(() => {
      setMapReady(false);
      setMap(null);
      ml.remove();
    });
  });

  // ── Reactive node source updates (nodes load / distances change) ──
  createEffect(() => {
    const m = map();
    if (!m || !mapReady()) return;
    const src = m.getSource(POI_SOURCE_ID) as GeoJSONSource | undefined;
    if (!src) return;
    src.setData(buildPoiCollection(nodeStore.nodes(), nodeView.distances()));
  });

  // ── Reactive player position + range ring ─────────────────────
  createEffect(() => {
    const m = map();
    if (!m || !mapReady()) return;
    const fix = geolocation.latest();
    (m.getSource(PLAYER_SOURCE_ID) as GeoJSONSource | undefined)?.setData(
      buildPlayerCollection(fix),
    );
    (m.getSource(RANGE_SOURCE_ID) as GeoJSONSource | undefined)?.setData(
      buildRangeCollection(geolocation.state() === 'tracking' ? fix : null),
    );
  });

  // ── Center on the player once, on the first settled fix ───────
  // Center-once (not continuous follow) avoids easeTo churn at ~1 Hz fixes;
  // the RECENTER button below lets the operator re-center on demand. On a
  // MAP remount `centeredOnce` resets, so returning to MAP re-centers — a
  // sensible default. (Flip to follow-on-every-fix here if desired.)
  let centeredOnce = false;
  createEffect(() => {
    const m = map();
    if (!m || !mapReady()) return;
    const fix = geolocation.latest();
    if (!fix || !fix.settled || centeredOnce) return;
    centeredOnce = true;
    m.easeTo({ center: [fix.lng, fix.lat], zoom: Math.max(m.getZoom(), INITIAL_ZOOM), duration: 800 });
  });

  const recenter = () => {
    const m = map();
    const fix = geolocation.latest();
    if (m && fix) {
      m.easeTo({ center: [fix.lng, fix.lat], zoom: Math.max(m.getZoom(), INITIAL_ZOOM), duration: 500 });
    }
  };

  // ── Reactive visibility / styling driven by tracker state ────
  createEffect(() => {
    const m = map();
    if (!m || !mapReady()) return;
    const state = geolocation.state();
    const visible = state === 'acquiring' || state === 'tracking';
    const layers = [PLAYER_DOT_LAYER_ID, PLAYER_PULSE_LAYER_ID, PLAYER_HEADING_LAYER_ID];
    for (const id of layers) {
      m.setLayoutProperty(id, 'visibility', visible ? 'visible' : 'none');
    }
    // Dot is muted during acquire so the operator can see "we have a fix
    // but it hasn't settled yet" without the cell flickering.
    m.setPaintProperty(PLAYER_DOT_LAYER_ID, 'circle-opacity', state === 'tracking' ? 1 : 0.4);
    m.setPaintProperty(PLAYER_DOT_LAYER_ID, 'circle-color', PLAYER_DOT_COLOR);
  });

  // ── Pulse only while tracking ────────────────────────────────
  createEffect(() => {
    const m = map();
    if (!m || !mapReady()) return;
    if (geolocation.state() !== 'tracking') {
      m.setPaintProperty(PLAYER_PULSE_LAYER_ID, 'circle-radius', PLAYER_PULSE_MIN_R);
      m.setPaintProperty(PLAYER_PULSE_LAYER_ID, 'circle-opacity', 0);
      return;
    }
    let phase = false;
    const step = () => {
      phase = !phase;
      m.setPaintProperty(
        PLAYER_PULSE_LAYER_ID,
        'circle-radius',
        phase ? PLAYER_PULSE_MAX_R : PLAYER_PULSE_MIN_R,
      );
      m.setPaintProperty(PLAYER_PULSE_LAYER_ID, 'circle-opacity', phase ? 0 : 0.55);
    };
    step();
    const id = setInterval(step, PULSE_PERIOD_MS / 2);
    onCleanup(() => clearInterval(id));
  });

  // ── HUD overlay ──────────────────────────────────────────────
  // 'ready' = hackable (target/dual) and in range (ENGAGE enabled);
  // 'out-of-range' = hackable but too far; 'non-target' = a pure shop/service.
  const engageState = createMemo<'ready' | 'out-of-range' | 'non-target'>(() => {
    const n = featured();
    if (!n || !nodeView.isHackable(n)) return 'non-target';
    return nodeView.isInRange(n.id) ? 'ready' : 'out-of-range';
  });

  // A featured node is a vendor (has a TRANSACT action) when it's not a pure target.
  const isVendor = (n: GameNode): boolean => n.role !== 'target';

  const onEngage = () => {
    const n = featured();
    if (n && nodeView.isEngageable(n)) props.onTargetSelect?.(nodeToTarget(n));
  };

  const onTransact = () => {
    const n = featured();
    if (n && isVendor(n)) props.onVendorOpen?.(n);
  };

  return (
    <div
      style={{ position: 'relative', flex: 1, overflow: 'hidden', background: 'var(--sf-void)' }}
    >
      <div
        ref={containerRef}
        style={{ position: 'absolute', inset: '0', width: '100%', height: '100%' }}
      />

      <div
        style={{
          position: 'absolute',
          top: '12px',
          left: '10px',
          'pointer-events': 'none',
        }}
      >
        <span class="t-label">NODES · {nodeStore.nodes().length}</span>
        <div style={{ 'margin-top': '4px', display: 'flex', 'align-items': 'center', gap: '6px' }}>
          <span class="t-micro" style={{ color: 'var(--fg-mute)' }}>HEAT</span>
          <span
            class="t-mono t-tab"
            style={{ 'font-size': '11px', color: `var(--${HEAT_TONE[heat.label()]})` }}
          >
            {Math.round(heat.value())}
          </span>
          <span class="t-micro" style={{ color: `var(--${HEAT_TONE[heat.label()]})` }}>
            {heat.label()}
          </span>
        </div>
      </div>

      <div
        style={{
          position: 'absolute',
          top: '12px',
          right: '10px',
          display: 'flex',
          'flex-direction': 'column',
          gap: '6px',
          'align-items': 'flex-end',
          'pointer-events': 'none',
        }}
      >
        <Show when={signalBadgeFor(geolocation.state())}>
          {(badge) => (
            <span
              class={badge().pulse ? 'pulse' : ''}
              // Explicitly inert: the parent already has pointer-events:
              // none, but iOS Safari has produced reports of touches near
              // the badge causing it to disappear. Belt-and-suspenders.
              // Opacity is set inline ONLY for the SIGNAL LIVE fade-out;
              // during ACQUIRING the inline opacity would override the
              // .pulse keyframe (CSS animations lose to inline styles),
              // leaving the pulse silent.
              style={{
                ...(geolocation.state() === 'tracking'
                  ? { opacity: signalLiveVisible() ? 1 : 0 }
                  : {}),
                transition: 'opacity var(--dur-slow) var(--ease)',
                'pointer-events': 'none',
                'user-select': 'none',
                '-webkit-user-select': 'none',
                '-webkit-touch-callout': 'none',
              }}
              aria-hidden={
                geolocation.state() === 'tracking' && !signalLiveVisible() ? true : false
              }
            >
              <Badge tone={badge().tone}>{badge().label}</Badge>
            </span>
          )}
        </Show>
        <Show
          when={
            geolocation.state() === 'idle' &&
            (permState() === 'prompt' || permState() === 'unsupported') &&
            prePromptDeclined() &&
            !showPrePrompt()
          }
        >
          <button
            class="btn"
            style={{
              'pointer-events': 'auto',
              padding: '6px 10px',
              'font-size': '11px',
              'letter-spacing': '0.08em',
            }}
            onClick={onAcquireSignalClick}
          >
            <Glyph name="signal" size={12} /> ACQUIRE SIGNAL
          </button>
        </Show>
        <Show when={usingPlaceholder}>
          <Badge tone="warn">NO TILES</Badge>
        </Show>
      </div>

      <Show when={showDeniedBanner()}>
        <div
          style={{
            position: 'absolute',
            top: '12px',
            left: '10px',
            right: '10px',
            'pointer-events': 'auto',
            'z-index': 4,
          }}
        >
          <div
            class="panel"
            style={{
              padding: '8px 10px',
              display: 'flex',
              'flex-direction': 'column',
              gap: '6px',
              'border-color': 'var(--hostile-line)',
              background: 'var(--hostile-bg)',
            }}
          >
            <div style={{ display: 'flex', 'align-items': 'center', gap: '6px' }}>
              <Glyph name="lock" size={12} color="var(--hostile)" />
              <span
                class="t-mono"
                style={{ 'font-size': '11px', color: 'var(--hostile)', 'letter-spacing': '0.06em' }}
              >
                LOCATION SIGNAL BLOCKED
              </span>
              <span style={{ flex: 1 }} />
              <button
                onClick={() => setBannerDismissed(true)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--fg-mute)',
                  cursor: 'pointer',
                  padding: 0,
                  display: 'flex',
                }}
                aria-label="Dismiss"
              >
                <Glyph name="x" size={12} />
              </button>
            </div>
            <div
              class="t-micro"
              style={{ color: 'var(--fg-mute)', 'line-height': '1.5', 'letter-spacing': '0.04em' }}
            >
              Location signal blocked. To enable: open iOS Settings → Apps → Safari → Location →
              Allow. Then tap RELOAD below.
            </div>
            <div style={{ display: 'flex', gap: '6px' }}>
              <Btn
                variant="primary"
                onClick={onReloadClick}
                disabled={reloading()}
                style={{
                  flex: 1,
                  'justify-content': 'center',
                  padding: '6px 10px',
                  'font-size': '11px',
                }}
              >
                <Show
                  when={reloading()}
                  fallback={
                    <>
                      <Glyph name="signal" size={12} /> RELOAD
                    </>
                  }
                >
                  RELOADING…
                </Show>
              </Btn>
            </div>
          </div>
        </div>
      </Show>

      <Show when={showNodeError()}>
        <div
          style={{
            position: 'absolute',
            top: '12px',
            left: '10px',
            right: '10px',
            'pointer-events': 'auto',
            'z-index': 4,
          }}
        >
          <div
            class="panel"
            style={{
              padding: '8px 10px',
              display: 'flex',
              'flex-direction': 'column',
              gap: '6px',
              background: 'var(--warn-bg)',
            }}
          >
            <div style={{ display: 'flex', 'align-items': 'center', gap: '6px' }}>
              <Glyph name="cpu" size={12} color="var(--warn)" />
              <span
                class="t-mono"
                style={{ 'font-size': '11px', color: 'var(--warn)', 'letter-spacing': '0.06em' }}
              >
                NODE DATA UNAVAILABLE
              </span>
              <span style={{ flex: 1 }} />
              <Badge tone="warn">FETCH</Badge>
            </div>
            <div
              class="t-micro"
              style={{ color: 'var(--fg-mute)', 'line-height': '1.5', 'letter-spacing': '0.04em' }}
            >
              Could not load the grid node map. The basemap and your position still work. Retry the
              fetch below.
            </div>
            <div style={{ display: 'flex', gap: '6px' }}>
              <Btn
                variant="primary"
                onClick={() => void nodeStore.load()}
                disabled={nodeStore.status() === 'loading'}
                style={{
                  flex: 1,
                  'justify-content': 'center',
                  padding: '6px 10px',
                  'font-size': '11px',
                }}
              >
                <Show when={nodeStore.status() === 'loading'} fallback={<>RETRY</>}>
                  RETRYING…
                </Show>
              </Btn>
            </div>
          </div>
        </div>
      </Show>

      {/* RECENTER is a floating map control pinned to the right edge, mid-
          height: clear of the top signal badges and the bottom node card /
          ENGAGE strip so the two never collide at mobile width. */}
      <Show when={geolocation.state() === 'tracking' && geolocation.latest()}>
        <button
          class="btn"
          style={{
            position: 'absolute',
            top: '50%',
            right: '10px',
            transform: 'translateY(-50%)',
            'pointer-events': 'auto',
            padding: '6px 10px',
            'font-size': '11px',
            'letter-spacing': '0.08em',
          }}
          onClick={recenter}
        >
          <Glyph name="target" size={12} /> RECENTER
        </button>
      </Show>

      <Show when={featured()}>
        {(node) => (
          <div
            style={{
              position: 'absolute',
              bottom: '12px',
              left: '10px',
              right: '10px',
              display: 'flex',
              gap: '8px',
              'align-items': 'stretch',
              'pointer-events': 'none',
            }}
          >
            <div
              class="panel"
              style={{
                flex: 1,
                padding: '8px 10px',
                display: 'flex',
                'flex-direction': 'column',
                gap: '4px',
                'pointer-events': 'auto',
              }}
            >
              <div style={{ display: 'flex', 'align-items': 'center', gap: '6px' }}>
                <span
                  style={{
                    width: '8px',
                    height: '8px',
                    background: ROLE_COLOR[node().role],
                    display: 'inline-block',
                  }}
                />
                <span class="t-mono" style={{ 'font-size': '11px', color: 'var(--fg)' }}>
                  {node().label}
                </span>
                <span style={{ flex: 1 }} />
                <Show when={engageState() === 'ready'}>
                  <Badge tone="action">IN RANGE</Badge>
                </Show>
                <Show when={engageState() === 'out-of-range'}>
                  <Badge tone="warn">OUT OF RANGE</Badge>
                </Show>
                <Badge tone={ROLE_BADGE_TONE[node().role]}>
                  {node().role === 'target'
                    ? node().tier
                    : node().role === 'dual'
                      ? `DUAL ${node().tier}`
                      : ROLE_LABEL[node().role]}
                </Badge>
              </div>
              {/* Hackable nodes (target/dual) show hack intel; pure vendors show
                  a trade hint. Dual nodes get an extra "hack or trade" line. */}
              <Show
                when={nodeView.isHackable(node())}
                fallback={
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <div class="t-micro">
                      DIST{' '}
                      <span style={{ color: 'var(--data)' }}>{formatDistance(featuredDist())}</span>
                    </div>
                    <div class="t-micro" style={{ color: 'var(--fg-dim)' }}>
                      {VENDOR_HINT[node().role]}
                    </div>
                  </div>
                }
              >
                <div style={{ display: 'flex', gap: '12px' }}>
                  <div class="t-micro">
                    DIST{' '}
                    <span style={{ color: 'var(--data)' }}>{formatDistance(featuredDist())}</span>
                  </div>
                  <div class="t-micro">
                    ICE{' '}
                    <span style={{ color: `var(--${ICE_TONE[node().iceRating]})` }}>
                      {node().iceRating}
                    </span>
                  </div>
                  <div class="t-micro">
                    REW <span style={{ color: 'var(--action)' }}>{node().reward}</span>
                  </div>
                </div>
              </Show>
              <Show when={node().role === 'dual'}>
                <div class="t-micro" style={{ color: 'var(--fg-dim)' }}>
                  {VENDOR_HINT.dual}
                </div>
              </Show>
            </div>
            {/* Action column: ENGAGE (hackable, range-gated) and/or TRADE
                (vendor). Dual nodes show both, stacked. */}
            <div
              style={{
                display: 'flex',
                'flex-direction': 'column',
                gap: '6px',
                'justify-content': 'center',
                'pointer-events': 'auto',
              }}
            >
              <Show when={nodeView.isHackable(node())}>
                <Btn
                  variant="primary"
                  disabled={engageState() !== 'ready'}
                  style={{ 'justify-content': 'center' }}
                  onClick={onEngage}
                >
                  <Glyph name="target" size={12} /> ENGAGE
                </Btn>
              </Show>
              <Show when={isVendor(node())}>
                <Btn style={{ 'justify-content': 'center' }} onClick={onTransact}>
                  <Glyph name="coin" size={12} /> TRADE
                </Btn>
              </Show>
            </div>
          </div>
        )}
      </Show>

      <Show when={showPrePrompt()}>
        <GeolocationPrePrompt
          onContinue={() => void onPrePromptContinue()}
          onDecline={onPrePromptDecline}
        />
      </Show>
    </div>
  );
}
